import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { DialogTextActionButton } from "../../Base/DialogTextActionButton";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  AdminPanelSettings as AdminIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import {
  manageAdminRights,
  listAdminUsers,
} from "../../../services/firebaseCloudFunctions";

interface AdminUser {
  uid: string;
  email: string;
  displayName: string | null;
  emailVerified: boolean;
  disabled: boolean;
  creationTime: string;
  lastSignInTime: string | null;
}

const AdminRights: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: "grant" | "revoke";
    email: string;
  }>({ open: false, action: "grant", email: "" });

  // Charger la liste des admins au montage
  useEffect(() => {
    loadAdminUsers();
  }, []);

  const loadAdminUsers = async () => {
    setLoadingUsers(true);
    try {
      const result = await listAdminUsers();
      const data = result.data as { success: boolean; adminUsers: AdminUser[] };
      if (data.success) {
        setAdminUsers(data.adminUsers);
      }
    } catch (error) {
      console.error("Erreur lors du chargement des admins:", error);
      setMessage({
        type: "error",
        text: "Erreur lors du chargement des administrateurs",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleGrantAdmin = async () => {
    if (!email.trim()) {
      setMessage({ type: "error", text: "Veuillez saisir une adresse email" });
      return;
    }
    setConfirmDialog({ open: true, action: "grant", email: email.trim() });
  };

  const handleRevokeAdmin = (userEmail: string) => {
    setConfirmDialog({ open: true, action: "revoke", email: userEmail });
  };

  const handleConfirmAction = async () => {
    const { action, email: targetEmail } = confirmDialog;
    setLoading(true);
    setMessage(null);

    try {
      const result = await manageAdminRights({ action, email: targetEmail });
      const data = result.data as { success: boolean; message: string };

      if (data.success) {
        setMessage({ type: "success", text: data.message });
        setEmail("");
        // Recharger la liste des admins
        await loadAdminUsers();
      }
    } catch (error: any) {
      console.error("Erreur lors de la gestion des droits admin:", error);
      const errorMessage =
        error.message ||
        "Erreur lors de la gestion des droits d'administration";
      setMessage({ type: "error", text: errorMessage });
    } finally {
      setLoading(false);
      setConfirmDialog({ open: false, action: "grant", email: "" });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 2 }}
      >
        <AdminIcon color="primary" />
        Gestion des droits d'administration
      </Typography>

      {/* Message d'alerte */}
      {message && (
        <Alert
          severity={message.type}
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Section d'ajout d'admin */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Accorder les droits d'administration
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Adresse email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="exemple@email.com"
              sx={{ flexGrow: 1 }}
              disabled={loading}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleGrantAdmin}
              disabled={loading || !email.trim()}
            >
              {loading ? <CircularProgress size={20} /> : "Accorder les droits"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Section liste des admins */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              Administrateurs actuels ({adminUsers.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAdminUsers}
              disabled={loadingUsers}
            >
              Actualiser
            </Button>
          </Box>

          {loadingUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : adminUsers.length === 0 ? (
            <Alert severity="info">Aucun administrateur trouvé</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Nom d'affichage</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Création</TableCell>
                    <TableCell>Dernière connexion</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {adminUsers.map((user) => (
                    <TableRow key={user.uid}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <EmailIcon color="action" fontSize="small" />
                          <Typography variant="body2" fontWeight="medium">
                            {user.email}
                          </Typography>
                          {user.emailVerified && (
                            <Chip
                              label="Vérifié"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {user.displayName ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <PersonIcon color="action" fontSize="small" />
                            {user.displayName}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Non défini
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.disabled ? "Désactivé" : "Actif"}
                          color={user.disabled ? "error" : "success"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ScheduleIcon color="action" fontSize="small" />
                          <Typography variant="body2">
                            {formatDate(user.creationTime)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {user.lastSignInTime ? (
                          <Typography variant="body2">
                            {formatDate(user.lastSignInTime)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Jamais connecté
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Révoquer les droits d'administration">
                          <IconButton
                            color="error"
                            onClick={() => handleRevokeAdmin(user.email)}
                            disabled={loading}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmation */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, action: "grant", email: "" })
        }
      >
        <DialogTitle>
          {confirmDialog.action === "grant"
            ? "Accorder les droits"
            : "Révoquer les droits"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === "grant"
              ? `Êtes-vous sûr de vouloir accorder les droits d'administration à ${confirmDialog.email} ?`
              : `Êtes-vous sûr de vouloir révoquer les droits d'administration de ${confirmDialog.email} ?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <DialogTextActionButton
            onClick={() =>
              setConfirmDialog({ open: false, action: "grant", email: "" })
            }
            disabled={loading}
          >
            Annuler
          </DialogTextActionButton>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.action === "grant" ? "primary" : "error"}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRights;
